import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, Building2, ArrowLeftRight, Smartphone, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Reveal, SectionHead } from "./Section";
import { IMG, apiError, CONTACT_EMAIL, SETUP_MAILTO } from "./homeData";

const EMPTY = { name: "", company: "", email: "", phone: "", crew_size: "", message: "", website: "" };
const TOPICS = [[Building2, "Setting up more than one depot"], [ArrowLeftRight, "Moving across from spreadsheets or another app"], [Smartphone, "Getting your crew onto their phones"]];

const KINDS = [["setup", "Get set up"], ["feedback", "Give feedback"], ["question", "Ask a question"]];
const COPY = {
  setup: ["Tell us about your company, trade and crew — we'll get you signed up and running.", "Send set-up request"],
  feedback: ["What works, what doesn't, and what would make your week easier? Every message is read by the team.", "Send feedback"],
  question: ["Ask us anything about Cross Jobs…", "Send question"],
};

const Kind = ({ topic, onTopic }) => (
  <div className="grid grid-cols-3 gap-1 rounded-full bg-slate-200/70 p-1" role="radiogroup" aria-label="What's this about?" data-testid="enquiry-topic">
    {KINDS.map(([v, label]) => (
      <button key={v} type="button" role="radio" aria-checked={topic === v} onClick={() => onTopic(v)} data-testid={`enquiry-topic-${v}`}
        className={`rounded-full px-2 py-2 text-xs sm:text-sm font-semibold transition-colors ${topic === v ? "bg-[#0B1120] text-white" : "text-slate-600 hover:text-[#0F172A]"}`}>{label}</button>
    ))}
  </div>
);

const F = ({ label, id, children }) => <div className="space-y-2"><Label htmlFor={id} className="text-slate-700">{label}</Label>{children}</div>;

const Sent = ({ name, feedback, again }) => (
  <div className="py-10 text-center" data-testid="enquiry-success">
    <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
    <h3 className="font-display mt-4 text-2xl font-bold">Thanks{feedback ? " for the feedback" : ""}, {name.split(" ")[0]}.</h3>
    <p className="mt-2 text-slate-600">Your message is on its way to the Cross Jobs team. We&apos;ll reply to your email.</p>
    <button onClick={again} data-testid="enquiry-again-btn" className="mt-6 font-semibold text-[#1E3A5F] hover:text-[#EA580C] transition-colors">Send another message</button>
  </div>
);

export const Contact = ({ topic = "setup", onTopic }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.sendEnquiry({ ...f, topic }); setSent({ name: f.name, topic }); setF(EMPTY); }
    catch (err) { toast.error(apiError(err, "We couldn't send your message — please try again.")); }
    finally { setBusy(false); }
  };

  return (
    <section id="contact" className="scroll-mt-16 bg-white py-24 lg:py-32" data-testid="home-contact">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.15fr] gap-14">
        <div>
          <SectionHead num="09" eyebrow="Get in touch" title="Email us today. We'll get you running." sub="Tell us about your crew and we'll get your company signed up and set up the right way. Already trying Cross Jobs? We'd love your feedback." />
          <ul className="mt-10 space-y-4">
            {TOPICS.map(([Icon, t]) => <li key={t} className="flex items-center gap-4 text-slate-700"><span className="w-10 h-10 rounded-xl bg-[#1E3A5F] text-white grid place-items-center"><Icon className="w-5 h-5" /></span>{t}</li>)}
          </ul>
          <a href={SETUP_MAILTO} data-testid="home-contact-email"
            className="group mt-10 inline-flex items-center gap-3 rounded-full border border-slate-200 pl-2 pr-5 py-2 font-semibold text-[#1E3A5F] hover:border-[#EA580C] hover:text-[#EA580C] transition-colors">
            <span className="w-9 h-9 rounded-full bg-[#EA580C] text-white grid place-items-center"><Mail className="w-4 h-4" /></span>
            {CONTACT_EMAIL}
          </a>
          <div className="mt-10 hidden lg:block h-64 rounded-2xl bg-cover bg-center" style={{ backgroundImage: `url(${IMG.melbourne})` }} role="img" aria-label="Worker in hi-vis outside Flinders Street Station, Melbourne" />
        </div>
        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 sm:p-10 shadow-[0_30px_60px_-40px_rgba(15,23,42,0.4)]">
            {sent ? <Sent name={sent.name} feedback={sent.topic === "feedback"} again={() => setSent(null)} /> : (
              <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
                <Kind topic={topic} onTopic={onTopic} />
                <div className="grid sm:grid-cols-2 gap-5">
                  <F label="Your name" id="enq-name"><Input id="enq-name" required maxLength={100} value={f.name} onChange={set("name")} placeholder="Jess Murray" data-testid="enquiry-name" /></F>
                  <F label="Company name" id="enq-company"><Input id="enq-company" maxLength={120} value={f.company} onChange={set("company")} placeholder="Southern Cross Plastering" data-testid="enquiry-company" /></F>
                  <F label="Email" id="enq-email"><Input id="enq-email" type="email" required value={f.email} onChange={set("email")} placeholder="you@company.com.au" data-testid="enquiry-email" /></F>
                  <F label="Phone" id="enq-phone"><Input id="enq-phone" type="tel" maxLength={40} value={f.phone} onChange={set("phone")} placeholder="04xx xxx xxx" data-testid="enquiry-phone" /></F>
                </div>
                <F label="Crew size" id="enq-crew">
                  <Select value={f.crew_size} onValueChange={(v) => setF((x) => ({ ...x, crew_size: v }))}>
                    <SelectTrigger id="enq-crew" data-testid="enquiry-crew-size"><SelectValue placeholder="How many workers?" /></SelectTrigger>
                    <SelectContent>
                      {["1–5", "6–15", "16–40", "40+"].map((v) => <SelectItem key={v} value={v} data-testid={`enquiry-crew-${v}`}>{v} workers</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Message" id="enq-msg"><Textarea id="enq-msg" required rows={5} maxLength={4000} value={f.message} onChange={set("message")} placeholder={COPY[topic][0]} data-testid="enquiry-message" /></F>
                <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
                <button type="submit" disabled={busy} data-testid="enquiry-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#EA580C] h-12 font-bold text-white hover:bg-[#C2410C] disabled:opacity-60 transition-colors">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {COPY[topic][1]}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
